import { isValid } from 'date-fns'; 

/**
 * Bezpiecznie pobiera i parsuje globalną konfigurację z localStorage.
 * Zwraca obiekt config lub null, jeśli nic nie ma lub wystąpił błąd.
 */
export const getGlobalConfig = () => {
  try {
    const configStr = localStorage.getItem('global_config');
    if (configStr) {
      return JSON.parse(configStr);
    }
  } catch (e) {
    console.error("Błąd parsowania global_config:", e);
  }
  return null; // Zwróć null w przypadku błędu lub braku configu
};

/**
 * Formatuje cenę z groszy (liczba całkowita) na string "XX.YY zł".
 * @param {number} grosze - Cena w groszach (np. 8999)
 * @returns {string | null} - Sformatowana cena (np. "89.99 zł") lub null
 */
export const formatPrice = (grosze) => {
  if (typeof grosze !== 'number' || isNaN(grosze)) {
    return null;
  }
  return `${(grosze / 100).toFixed(2).replace('.', ',')} zł`;
};

/**
 * Helper do pobierania surowych cen w groszach.
 * Używane do logiki płatności. Centralne miejsce na fallbacki.
 */
export const getRawPricesInGrosze = () => {
  const config = getGlobalConfig();
  const prices = config?.content_variables?.prices_in_grosze;

  return {
      plan_1m: prices?.plan_1m || 2999,
      plan_3m: prices?.plan_3m || 6999,
      plan_do_matury: prices?.plan_do_matury || 7999,
      plan_poprawka_2026: prices?.plan_poprawka_2026 || 6999,
      matura_pp_2027_regular: prices?.matura_pp_2027_regular || 14999,
      matura_pp_2027_promo: prices?.matura_pp_2027_promo || 11999,
      // Rozszerzenie — samo albo w pakiecie z podstawą.
      matura_pr_2027_regular: prices?.matura_pr_2027_regular || 17999,
      matura_pr_2027_promo: prices?.matura_pr_2027_promo || 14999,
      // Pakiet PP+PR — nazwa "pp_pr" wymuszona przez niemodyfikowalny Product ID w App Store.
      matura_pp_pr_2027_regular: prices?.matura_pp_pr_2027_regular || 28999,
      matura_pp_pr_2027_promo: prices?.matura_pp_pr_2027_promo || 19999,
      // Miesięczne warianty: podstawa / rozszerzenie / oba.
      '1_miesiac': prices?.['1_miesiac'] || 2999,
      '1_miesiac_pr': prices?.['1_miesiac_pr'] || 3999,
      '1_miesiac_pp_pr': prices?.['1_miesiac_pp_pr'] || 4999,
  };
};

/**
 * Helper, który od razu pobiera i formatuje wszystkie ceny.
 * Używane do wyświetlania.
 */
export const getFormattedPrices = () => {
  const rawPrices = getRawPricesInGrosze();
  
  return {
    plan_1m: formatPrice(rawPrices.plan_1m),
    plan_3m: formatPrice(rawPrices.plan_3m),
    plan_do_matury: formatPrice(rawPrices.plan_do_matury),
    plan_poprawka_2026: formatPrice(rawPrices.plan_poprawka_2026),
    matura_pp_2027_regular: formatPrice(rawPrices.matura_pp_2027_regular),
    matura_pp_2027_promo: formatPrice(rawPrices.matura_pp_2027_promo),
    matura_pr_2027_regular: formatPrice(rawPrices.matura_pr_2027_regular),
    matura_pr_2027_promo: formatPrice(rawPrices.matura_pr_2027_promo),
    matura_pp_pr_2027_regular: formatPrice(rawPrices.matura_pp_pr_2027_regular),
    matura_pp_pr_2027_promo: formatPrice(rawPrices.matura_pp_pr_2027_promo),
    '1_miesiac': formatPrice(rawPrices['1_miesiac']),
    '1_miesiac_pr': formatPrice(rawPrices['1_miesiac_pr']),
    '1_miesiac_pp_pr': formatPrice(rawPrices['1_miesiac_pp_pr']),
  };
};

/**
 * Pobiera datę matury z configa.
 * Zwraca obiekt Date.
 */
export const getMaturaDate = () => {
  const config = getGlobalConfig();
  const timestamp = config?.content_variables?.matura_timestamp_utc;

  if (timestamp) {
    const maturaDate = new Date(timestamp * 1000);
    if (isValid(maturaDate)) {
      return maturaDate;
    }
  }

  console.warn("Could not find 'matura_timestamp_utc' in config. Using fallback date '2027-05-14'.");
  return new Date('2027-05-14');
};

/**
 * Koniec dostepu "do matury" do wyswietlania — backend przyznaje do 14.05.2027 23:59 UTC
 * (payment_views.py). Data lokalna, zeby w PL nie pokazalo sie "15 maja".
 * Bramkowanie ofert dalej idzie po getMaturaDate().
 */
export const getMaturaAccessEndDate = () => new Date(2027, 4, 14);